import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Card,
  CardContent,
  Modal,
  Divider,
  FormGroup,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  CircularProgress,
} from '@mui/material';
import MpTheme from '../../../config/MpTheme';
import { fonts, MpColorPalette } from '../../../theme/theme';
import CloseIcon from '@mui/icons-material/Close';
import { API } from 'aws-amplify';
import { Toast } from '../../toast/toast';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`broker-tabpanel-${index}`}
      aria-labelledby={`broker-tab-${index}`}
      {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Updated Interfaces to match the new data structure
interface FieldConfig {
  key: string;
  enabled: boolean;
}

interface EmailTemplateFields {
  name: FieldConfig[];
  personal: FieldConfig[];
  address: FieldConfig[];
  contact: FieldConfig[];
  linkedin_url: FieldConfig[];
  // TODO: Commented out for future use
  // profile_url: FieldConfig[]; // Keeping the profile_url field
}

interface RemovalConfig {
  email_template_fields: EmailTemplateFields;
}

interface Broker {
  id: number;
  name: string;
  display_name: string;
  email: string;
  is_active: number;
  created_at: string;
  updated_at: string;
  removal_config: RemovalConfig;
}

const formatFieldKey = (key: string): string => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const BrokerSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<Broker | null>(null);
  const [configData, setConfigData] = useState<EmailTemplateFields | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [allFieldsDisabled, setAllFieldsDisabled] = useState(false);

  useEffect(() => {
    const fetchBrokers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await API.get('CorpOnboarding', '/brokers', {});
        setBrokers(response);
      } catch (err) {
        console.error('Error fetching brokers:', err);
        setError('Failed to load brokers. Please try again later.');
        Toast.show({ type: 'error', text1: 'Failed to load brokers. Please try again later.' });
      } finally {
        setLoading(false);
      }
    };
    fetchBrokers();
  }, []);

  useEffect(() => {
    if (!configData) return;

    const allDisabled = Object.keys(configData).every((category) => {
      return configData[category as keyof EmailTemplateFields].every(
        (field) => !field.enabled,
      );
    });

    setAllFieldsDisabled(allDisabled);
  }, [configData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleConfigureClick = (broker: Broker) => {
    setSelectedBroker(broker);
    setConfigData(
      JSON.parse(JSON.stringify(broker.removal_config.email_template_fields)),
    );
    setModalOpen(true);
    setHasChanges(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBroker(null);
    setConfigData(null);
    setHasChanges(false);
  };

  const handleToggleChange = (
    category: keyof EmailTemplateFields,
    index: number,
  ) => {
    if (!configData) return;

    setConfigData((prev) => {
      if (!prev) return prev;

      // Shallow copy and toggle the enabled field
      const newData = { ...prev };
      newData[category] = [...prev[category]];
      newData[category][index] = {
        ...newData[category][index],
        enabled: !newData[category][index].enabled,
      };

      return newData;
    });

    setHasChanges(true);
  };

  const handleSaveConfig = async () => {
    if (!selectedBroker || !configData || !selectedBroker.removal_config) return;
    setSaving(true);
    setError(null);
    try {
      await API.patch('CorpOnboarding', `/brokers/${selectedBroker.name}/${selectedBroker.id}`, {
        body: {
          removal_config: {
            email_template_fields: configData,
          },
        },
      });

      setBrokers((prevBrokers) => {
        return prevBrokers.map((broker) => {
          if (broker.email === selectedBroker.email) {
            return {
              ...broker,
              removal_config: {
                ...broker.removal_config,
                email_template_fields: configData,
              },
            };
          }
          return broker;
        });
      });

      const brokerName = selectedBroker.display_name || selectedBroker.name;
      Toast.show({
        type: 'success',
        text1: `Broker configuration for "${brokerName}" saved successfully.`,
      });
      handleCloseModal();
    } catch (err) {
      console.error('Error saving broker configuration:', err);
      Toast.show({
        type: 'error',
        text1: 'Failed to save broker configuration. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '85%',
    maxWidth: 1000,
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column' as 'column',
    bgcolor: 'background.paper',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
    borderRadius: '16px',
    p: 0,
    zIndex: 9999,
    overflow: 'hidden',
    transition: 'all 0.3s ease-in-out',
  };

  const renderConfigSection = (
    title: string,
    category: keyof EmailTemplateFields,
  ) => {
    if (!configData || !configData[category]) return null;
    return (
      <Card
        elevation={0}
        sx={{
          mb: 4,
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          transition: 'all 0.25s ease-in-out',
          '&:hover': {
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
            borderColor: '#d0d0d0',
          },
          overflow: 'hidden',
        }}>
        <CardContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              color={MpColorPalette.purple[800]}
              fontWeight={600}
              sx={{ fontFamily: fonts.Jakarta.Bold.fontFamily }}>
              {title}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2.5 }} />
          <Grid container spacing={2.5}>
            {configData[category].map((field, index) => (
              <Grid item xs={12} sm={6} md={4} key={field.key}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.enabled}
                        onChange={() => handleToggleChange(category, index)}
                        color="secondary"
                        sx={{
                          '& .MuiSwitch-switchBase': {
                            '&.Mui-checked': {
                              color: MpTheme.colors.primary.default,
                              '& + .MuiSwitch-track': {
                                backgroundColor: MpTheme.colors.primary.default,
                                opacity: 0.5,
                              },
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: field.enabled ? 600 : 400,
                          transition: 'all 0.2s ease',
                          color: field.enabled
                            ? MpTheme.colors.primary.default
                            : 'text.primary',
                        }}>
                        {formatFieldKey(field.key)}
                      </Typography>
                    }
                    sx={{
                      py: 0.75,
                      px: 1,
                      borderRadius: 1.5,
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      },
                    }}
                  />
                </FormGroup>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Tabs and TabPanel setup remains the same */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 4,
          position: 'relative',
        }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="broker settings tabs"
          indicatorColor="secondary"
          textColor="primary"
          centered
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              backgroundColor: MpTheme.colors.primary.default,
            },
            '& .MuiTab-root': {
              transition: 'all 0.2s ease',
              mx: 1,
            },
          }}>
          <Tab
            label="Blast Email Brokers"
            sx={{
              fontWeight: tabValue === 0 ? 700 : 500,
              textTransform: 'none',
              fontSize: '1rem',
              color:
                tabValue === 0
                  ? MpTheme.colors.primary.default
                  : MpTheme.colors.gray.default,
              padding: '12px 24px',
              borderRadius: '8px 8px 0 0',
              '&:hover': {
                backgroundColor:
                  tabValue === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.03)',
                color:
                  tabValue === 0
                    ? MpTheme.colors.primary.default
                    : MpTheme.colors.primary[300],
              },
            }}
          />
          <Tab
            label="Other Brokers"
            disabled
            sx={{
              fontWeight: tabValue === 1 ? 700 : 500,
              textTransform: 'none',
              fontSize: '1rem',
              color: 'rgba(0, 0, 0, 0.38)',
              padding: '12px 24px',
              borderRadius: '8px 8px 0 0',
            }}
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card
          sx={{
            mb: 4,
            backgroundColor: '#f9f9f9',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
            position: 'relative',
          }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  backgroundColor: 'rgba(58, 21, 90, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                }}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0l-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z"
                    fill="#3a155a"
                  />
                </svg>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  mb: 0,
                  color: MpTheme.colors.primary.default,
                  fontWeight: 600,
                }}>
                Blast Email Brokers
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 7 }}>
              Configure email brokers to handle blast email communications.
              These brokers help manage email deliveries to your users.
            </Typography>
          </CardContent>
        </Card>

        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={8}
            sx={{ backgroundColor: '#fafafa', borderRadius: '12px' }}>
            <CircularProgress color="primary" size={40} thickness={4} />
            <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
              Loading brokers...
            </Typography>
          </Box>
        ) : error ? null : brokers.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 3,
              backgroundColor: '#fafafa',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              border: '1px dashed #ccc',
            }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No brokers found
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
              Add a broker to get started with email template configuration.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              overflow: 'hidden',
            }}>
            <Table sx={{ minWidth: 650 }} aria-label="broker table">
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: MpTheme.colors.primary.default,
                      py: 2,
                      pl: 3,
                    }}>
                    Broker Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: MpTheme.colors.primary.default,
                      py: 2,
                    }}>
                    Email
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: MpTheme.colors.primary.default,
                      py: 2,
                    }}>
                    Status
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: MpTheme.colors.primary.default,
                      py: 2,
                      pr: 3,
                    }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brokers.map((broker) => (
                  <TableRow
                    key={broker.id} // Use unique id as key
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': {
                        backgroundColor: '#f5f9ff',
                      },
                    }}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: 500, pl: 3, py: 2 }}>
                      {broker.display_name}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{broker.email}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box
                        component="span"
                        sx={{
                          px: 2,
                          py: 0.8,
                          borderRadius: '12px',
                          backgroundColor:
                            broker.is_active === 1 ? '#e6f7ed' : '#ffeaea',
                          color:
                            broker.is_active === 1 ? '#1e8e3e' : '#d93025',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          textTransform: 'capitalize',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}>
                        <Box
                          component="span"
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor:
                              broker.is_active === 1 ? '#1e8e3e' : '#d93025',
                          }}
                        />
                        {broker.is_active === 1 ? 'Active' : 'Inactive'}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 3, py: 2 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => handleConfigureClick(broker)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          fontWeight: 500,
                          px: 2.5,
                          py: 0.8,
                          borderColor: 'rgba(58, 21, 90, 0.3)',
                          color: MpTheme.colors.primary.default,
                          '&:hover': {
                            backgroundColor: 'rgba(58, 21, 90, 0.04)',
                            borderColor: MpTheme.colors.primary.default,
                          },
                        }}>
                        Configure
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="body1">
          This section is currently under development.
        </Typography>
      </TabPanel>

      {/* Configuration Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="broker-config-modal"
        sx={{
          zIndex: 9999,
          backdropFilter: 'blur(4px)',
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        }}>
        <Box
          sx={modalStyle}
          style={{
            opacity: modalOpen ? 1 : 0,
            transform: modalOpen
              ? 'translate(-50%, -50%) scale(1)'
              : 'translate(-50%, -50%) scale(0.9)',
          }}>
          {/* Modal Header */}
          <Box
            sx={{
              p: 3,
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Box>
              <Typography
                variant="h3"
                component="h2"
                color={MpTheme.colors.primary.default}
                sx={{ fontWeight: 800, mb: 0.5, lineHeight: 1.1 }}>
                Configure {selectedBroker?.display_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                Enable or disable fields for email templates.
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseModal}
              aria-label="close"
              sx={{
                color: 'grey.500',
                '&:hover': { color: MpTheme.colors.primary.default },
              }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box
            sx={{
              p: 4,
              flex: 1,
              overflowY: 'auto',
              maxHeight: 'calc(92vh - 140px)',
            }}>
            {configData && (
              <Box>
                {renderConfigSection('Name Fields', 'name')}
                {renderConfigSection('Personal Fields', 'personal')}
                {renderConfigSection('Address Fields', 'address')}
                {renderConfigSection('Contact Fields', 'contact')}
                {renderConfigSection('LinkedIn URL Fields', 'linkedin_url')}
                {/* Commented out for future use (Profile URL): renderConfigSection('Profile URL Fields', 'profile_url')} */}
              </Box>
            )}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              p: 3,
              gap: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
            }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCloseModal}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                fontWeight: 500,
              }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveConfig}
              disabled={saving || !hasChanges || allFieldsDisabled}
              startIcon={
                saving ? undefined : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.2L4.8 12 3.4 13.4 9 19 21 7l-1.4-1.4L9 16.2z" />
                  </svg>
                )
              }
              sx={{
                textTransform: 'none',
                minWidth: 130,
                borderRadius: '10px',
                fontWeight: 600,
              }}>
              {saving ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default BrokerSettings;
